import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { Product } from 'types/graphql-overrides';
import { PRODUCT_GROUP_SIZES } from './product-list.constants';

export type ProductRenderItem = {
  type: 'product';
  product: Product;
  index: number;
};

export type SlotRenderItem = {
  type: 'slot';
  component: React.ElementType;
};

export type RenderItem = ProductRenderItem | SlotRenderItem;

const DEFAULT_SLOT_POSITION = 6; // 0-indexed, position 7 in the grid

type GroupProductsByRowsArgs = {
  products: Array<Product>;
  isListView: boolean;
  rows: Array<number>;
  slotComponent?: React.ElementType | null;
  slotPosition?: number;
};

type GroupProductsByRows = {
  getItemsGroup: (groupIndex: number) => Array<RenderItem>;
  groupedItems: Array<Array<RenderItem>>;
  hasSlot: boolean;
};

/**
 * Custom hook to calculate items per row based on screen size.
 */
function useItemsPerRow(isListView: boolean): number {
  const [itemsPerRow, setItemsPerRow] = useState(1);
  const { sm, md, lg, xl, xxl } = PRODUCT_GROUP_SIZES.grid;

  const isSmall = useMediaQuery(`(min-width:${sm.breakpoint}px)`);
  const isMedium = useMediaQuery(`(min-width:${md.breakpoint}px)`);
  const isLarge = useMediaQuery(`(min-width:${lg.breakpoint}px)`);
  const isXLarge = useMediaQuery(`(min-width:${xl.breakpoint}px)`);
  const isXXLarge = useMediaQuery(`(min-width:${xxl.breakpoint}px)`);

  useEffect(() => {
    if (isListView) {
      setItemsPerRow(1);
    } else if (isXXLarge) {
      setItemsPerRow(xxl.rowSize);
    } else if (isXLarge) {
      setItemsPerRow(xl.rowSize);
    } else if (isLarge) {
      setItemsPerRow(lg.rowSize);
    } else if (isMedium) {
      setItemsPerRow(md.rowSize);
    } else if (isSmall) {
      setItemsPerRow(sm.rowSize);
    }
  }, [
    isLarge,
    isListView,
    isMedium,
    isSmall,
    isXLarge,
    isXXLarge,
    lg.rowSize,
    md.rowSize,
    sm.rowSize,
    xl.rowSize,
    xxl.rowSize,
  ]);

  return itemsPerRow;
}

export function useGroupProductsByRows({
  products,
  isListView,
  rows,
  slotComponent = null,
  slotPosition = DEFAULT_SLOT_POSITION,
}: GroupProductsByRowsArgs): GroupProductsByRows {
  const itemsPerRow = useItemsPerRow(isListView);
  const hasSlot = slotComponent !== null && products.length > slotPosition;

  // Build render items with optional slot injection
  const renderItems = useMemo((): RenderItem[] => {
    const items: RenderItem[] = products.map(
      (product, i): ProductRenderItem => ({
        type: 'product',
        product,
        index: i,
      })
    );

    if (hasSlot) {
      const slotItem: SlotRenderItem = { type: 'slot', component: slotComponent };
      items.splice(slotPosition, 0, slotItem);
    }

    return items;
  }, [products, hasSlot, slotComponent, slotPosition]);

  // Group render items by rows
  const groupedItems = useMemo(() => {
    const groups: Array<Array<RenderItem>> = [];
    let startIndex = 0;

    [...rows, renderItems.length].forEach((row) => {
      const endIndex = startIndex + itemsPerRow * row;
      groups.push(renderItems.slice(startIndex, endIndex));
      startIndex = endIndex;
    });

    return groups;
  }, [rows, renderItems, itemsPerRow]);

  const getItemsGroup = useCallback((groupIndex: number): Array<RenderItem> => groupedItems[groupIndex] ?? [], [
    groupedItems,
  ]);

  return {
    getItemsGroup,
    groupedItems,
    hasSlot,
  };
}
